import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Receipt, Plus, X, Camera, Loader, Trash2, ExternalLink, Save, RefreshCw,
  Mail, Send, ShieldAlert, RotateCcw, Search, FileText, ClipboardPaste,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { uprightJpegBytes } from '../utils/imageOrient';

// ── Receipts vault ──────────────────────────────────────────────────────────────
// Admin-only register of business receipts for tax time. Each receipt keeps its
// original scan/photo/PDF in the private 'receipts' bucket, with a Claude OCR read
// (vendor / date / total) for prefill. The point is retain + recall + email: if the
// ATO audits us or the accountant asks, we find it and email the original in a click.

const RECEIPTS_URL = '/.netlify/functions/receipts-sb';
const MAX_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

// Business/personal split rules. Coles, Woolworths & Harris Farm default to the
// 80/20 general rule (groceries are mostly personal); occasionally they're a
// 100% business purchase, so the drawer offers a dropdown to switch.
const DEFAULT_SPLIT = 'Personal 80% Business 20%';
const SPLIT_RULES = [DEFAULT_SPLIT, '100% Business'];
const SUPERMARKETS = ['coles', 'woolworths', 'harris farm'];
const isSupermarket = (v) => SUPERMARKETS.some((s) => (v || '').toLowerCase().includes(s));

const api = (action, payload = {}) =>
  fetch(RECEIPTS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtMoney = (n) => (n == null || n === '' ? '—' : (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }));

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
// File → base64. Images become upright baseline JPEGs (bakes iPhone EXIF rotation,
// downscales); PDFs pass through untouched.
async function fileToBase64(file) {
  if (file.type === 'application/pdf') {
    return { base64: arrayBufferToBase64(await file.arrayBuffer()), contentType: 'application/pdf' };
  }
  let contentType = 'image/jpeg';
  let ab = await uprightJpegBytes(file, 2000, 0.85);
  if (!ab) { ab = await file.arrayBuffer(); contentType = file.type || 'image/jpeg'; }
  return { base64: arrayBufferToBase64(ab), contentType };
}

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const th = { padding: '9px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

// ── Add / edit drawer ─────────────────────────────────────────────────────────
function ReceiptDrawer({ receipt, currentUser, onClose, onSaved, addNotification }) {
  const editing = !!receipt;
  const blank = { vendor: '', purchaseDate: '', amount: '', note: '' };
  const [form, setForm] = useState(receipt ? {
    vendor: receipt.vendor || '', purchaseDate: receipt.purchaseDate || '',
    amount: receipt.amount != null ? receipt.amount : '', note: receipt.note || '',
  } : blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [file, setFile] = useState(null); // { base64, contentType, dataUrl, isPdf } for a fresh upload
  const [ocrBusy, setOcrBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Touch/phone devices: no keyboard for Ctrl/Cmd+V and mobile browsers restrict
  // programmatic clipboard image reads, so the Paste button is a desktop-only affordance.
  const coarsePointer = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  const acceptFile = async (f) => {
    if (!f) return;
    const isImg = f.type.startsWith('image/');
    const isPdf = f.type === 'application/pdf';
    if (!isImg && !isPdf) { addNotification?.('Attach a photo or a PDF of the receipt', 'error'); return; }
    if (f.size > MAX_MB * 1.4 * 1024 * 1024 && isPdf) { addNotification?.(`That PDF is too large (keep under ${MAX_MB} MB)`, 'error'); return; }
    if (isImg && f.size > MAX_MB * 3 * 1024 * 1024) { addNotification?.('That image is very large — try again', 'error'); return; }
    setOcrBusy(true);
    try {
      const { base64, contentType } = await fileToBase64(f);
      setFile({ base64, contentType, isPdf, dataUrl: isPdf ? '' : `data:${contentType};base64,${base64}` });
      // OCR for prefill (no storage) — fill only empty fields so manual entries win.
      const r = await api('ocrReceiptImage', { base64, contentType });
      if (r.success && r.extracted) {
        const x = r.extracted;
        setForm((cur) => {
          const vendor = cur.vendor || x.vendor || '';
          return {
            ...cur,
            vendor,
            purchaseDate: cur.purchaseDate || (/^\d{4}-\d{2}-\d{2}$/.test(x.date) ? x.date : ''),
            amount: (cur.amount === '' && x.amount) ? x.amount : cur.amount,
            // Groceries default to the 80/20 rule (only if a note isn't already set).
            note: cur.note || (isSupermarket(vendor) ? DEFAULT_SPLIT : ''),
          };
        });
        addNotification?.('Receipt read — check the fields, then save', 'success');
      } else if (r.error) {
        addNotification?.(`Couldn't auto-read the receipt (${r.error}). Enter the details manually — the file still saves.`, 'info');
      }
    } catch (e) {
      addNotification?.(`Could not process the file: ${e.message}`, 'error');
    } finally { setOcrBusy(false); }
  };

  const onFilePick = (e) => { acceptFile(e.target.files?.[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer?.files?.[0]); };

  // Paste a receipt while the drawer is open (Cmd/Ctrl+V) — e.g. a screenshot or a
  // copied image/PDF. A ref keeps the listener pointed at the latest acceptFile.
  const acceptRef = useRef(acceptFile);
  acceptRef.current = acceptFile;
  useEffect(() => {
    const onPaste = (e) => {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (const it of items) {
        if (it.kind === 'file' && (it.type.startsWith('image/') || it.type === 'application/pdf')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); acceptRef.current(f); return; }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  // Explicit "Paste" button — reads the clipboard directly where the browser allows it.
  const pasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        addNotification?.('Press Ctrl/Cmd+V to paste the receipt here.', 'info'); return;
      }
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith('image/') || t === 'application/pdf');
        if (type) {
          const blob = await it.getType(type);
          const ext = type === 'application/pdf' ? 'pdf' : (type.split('/')[1] || 'png');
          acceptFile(new File([blob], `pasted.${ext}`, { type }));
          return;
        }
      }
      addNotification?.('No image found on the clipboard — copy a receipt first, or press Ctrl/Cmd+V.', 'info');
    } catch {
      addNotification?.("Couldn't read the clipboard — press Ctrl/Cmd+V instead.", 'info');
    }
  };

  const save = async () => {
    if (!form.vendor && !file && !editing) { addNotification?.('Add a receipt file or at least a vendor name', 'error'); return; }
    setSaving(true);
    try {
      const ext = file?.isPdf ? 'pdf' : 'jpg';
      const payload = {
        id: receipt?.id, ...form,
        createdBy: currentUser?.login || '',
        fileBase64: file?.base64, fileContentType: file?.contentType,
        fileName: file ? `${(form.vendor || 'receipt').replace(/[^\w.\- ]/g, '_')}_${form.purchaseDate || ''}.${ext}` : undefined,
      };
      const r = await api('saveReceipt', payload);
      if (!r.success) { addNotification?.(`Save failed: ${r.error || 'unknown error'}`, 'error'); return; }
      if (r.ocrError) addNotification?.(`Saved. Note: auto-read failed (${r.ocrError}) — you can Re-read from the list.`, 'info');
      else addNotification?.('Receipt saved', 'success');
      onSaved();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 520, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 26 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Receipts</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? `Edit — ${receipt.vendor || 'receipt'}` : 'Add a receipt'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Scan / upload */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{ border: `2px dashed ${dragOver ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: 16, marginBottom: 20, textAlign: 'center', background: dragOver ? 'rgba(201,168,76,0.06)' : 'transparent' }}
        >
          {file?.dataUrl ? (
            <div>
              <img src={file.dataUrl} alt="receipt" style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
              <div style={{ marginTop: 8 }}><button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><RotateCcw size={13} /> Replace</button></div>
            </div>
          ) : file?.isPdf ? (
            <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem' }}>
              <FileText size={26} style={{ color: 'var(--hp-gold)' }} />
              <div style={{ marginTop: 6 }}>PDF ready to save</div>
              <div style={{ marginTop: 8 }}><button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><RotateCcw size={13} /> Replace</button></div>
            </div>
          ) : editing && receipt.fileViewUrl ? (
            <div style={{ fontSize: '.85rem', color: 'var(--hp-dim)' }}>
              <a href={receipt.fileViewUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><ExternalLink size={13} /> View stored receipt</a>
              <div style={{ marginTop: 10 }}>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*,application/pdf" onChange={onFilePick} style={{ display: 'none' }} />
                  <RotateCcw size={13} /> Replace file
                </label>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', marginBottom: 10 }}>
                {ocrBusy ? <><Loader size={15} /> Reading the receipt…</>
                  : coarsePointer
                    ? 'Take a photo, or pick an existing receipt image or PDF — Claude reads the vendor, date & total for you.'
                    : 'Scan, drop, paste or upload a photo or PDF — Claude reads the vendor, date & total for you.'}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*,application/pdf" onChange={onFilePick} style={{ display: 'none' }} disabled={ocrBusy} />
                  <Camera size={14} /> {coarsePointer ? 'Photo / upload' : 'Scan / upload receipt'}
                </label>
                {!coarsePointer && (
                  <button className="btn btn-secondary btn-sm" onClick={pasteFromClipboard} disabled={ocrBusy}>
                    <ClipboardPaste size={14} /> Paste
                  </button>
                )}
              </div>
              {!coarsePointer && (
                <div style={{ color: 'var(--hp-dim)', fontSize: '.7rem', marginTop: 8 }}>Tip: copy a receipt image, then press Ctrl/Cmd+V.</div>
              )}
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Vendor / supplier</label>
            <input
              className="input" style={inputStyle} value={form.vendor}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, vendor: v, note: f.note || (isSupermarket(v) ? DEFAULT_SPLIT : '') })); }}
              placeholder="e.g. BP Alexandria"
            />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" className="input" style={inputStyle} value={form.purchaseDate} onChange={(e) => set('purchaseDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Total ($)</label>
            <input type="number" step="0.01" className="input" style={inputStyle} value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Business / personal split</label>
            <select
              className="input" style={inputStyle}
              value={SPLIT_RULES.includes(form.note) ? form.note : '__other'}
              onChange={(e) => { if (e.target.value !== '__other') set('note', e.target.value); }}
            >
              {SPLIT_RULES.map((r) => <option key={r} value={r}>{r}</option>)}
              <option value="__other">Other / custom…</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note / comment</label>
            <input className="input" style={inputStyle} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="e.g. Fuel — van AB12CD" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || ocrBusy}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save changes' : 'Save receipt'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Email-to-accountant modal ───────────────────────────────────────────────────
function EmailModal({ receipt, defaultEmail, onClose, onSent, addNotification }) {
  const [to, setTo] = useState(defaultEmail || '');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [remember, setRemember] = useState(!defaultEmail);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) { addNotification?.('Enter a valid email address', 'error'); return; }
    setSending(true);
    try {
      const r = await api('emailReceipt', { id: receipt.id, to: to.trim(), cc: cc.trim(), message, remember });
      if (!r.success) { addNotification?.(`Send failed: ${r.error || 'unknown error'}`, 'error'); return; }
      addNotification?.(`Receipt emailed to ${to.trim()}`, 'success');
      onSent?.(remember ? to.trim() : undefined);
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ width: 460, maxWidth: '100%', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Mail size={18} style={{ color: 'var(--hp-gold)' }} /> Email receipt</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ fontSize: '.85rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
          Sends the original {(receipt.contentType === 'application/pdf') ? 'PDF' : 'image'} of <strong style={{ color: 'var(--hp-text)' }}>{receipt.vendor || 'this receipt'}</strong>
          {receipt.purchaseDate ? ` (${fmtDate(receipt.purchaseDate)})` : ''}{receipt.amount != null ? ` · ${fmtMoney(receipt.amount)}` : ''} as an attachment.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>To (accountant)</label>
            <input className="input" style={inputStyle} type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="accountant@firm.com.au" />
          </div>
          <div>
            <label style={labelStyle}>CC (optional)</label>
            <input className="input" style={inputStyle} type="email" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="you@hollypaul.com" />
          </div>
          <div>
            <label style={labelStyle}>Message (optional)</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a note for the accountant…" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: 'var(--hp-dim)', cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Remember this address for next time
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={send} disabled={sending}>
            {sending ? <><Loader size={14} /> Sending…</> : <><Send size={14} /> Send to accountant</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ReceiptsPage() {
  const { currentUser, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [receipts, setReceipts] = useState([]);
  const [accountantEmail, setAccountantEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);   // null | { receipt } | { receipt: null }
  const [emailFor, setEmailFor] = useState(null); // receipt being emailed
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minAmt, setMinAmt] = useState('');
  const [maxAmt, setMaxAmt] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('listReceipts', { from: from || undefined, to: to || undefined });
      if (r.success) { setReceipts(r.receipts); setAccountantEmail(r.accountantEmail || ''); }
      else addNotification?.(`Could not load receipts: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification, from, to]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete the receipt from ${r.vendor || 'this vendor'}? This removes the stored file too.`)) return;
    const res = await api('deleteReceipt', { id: r.id });
    if (res.success) { addNotification?.('Receipt deleted', 'success'); load(); }
    else addNotification?.(`Delete failed: ${res.error || 'unknown error'}`, 'error');
  };

  const reRead = async (r) => {
    addNotification?.('Re-reading the receipt…', 'info');
    const res = await api('reReadReceipt', { id: r.id });
    if (res.success) { addNotification?.('Receipt re-read', 'success'); load(); }
    else addNotification?.(`Re-read failed: ${res.error || 'unknown error'}`, 'error');
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const min = minAmt === '' ? null : parseFloat(minAmt);
    const max = maxAmt === '' ? null : parseFloat(maxAmt);
    return receipts.filter((r) => {
      if (needle && !`${r.vendor} ${r.note}`.toLowerCase().includes(needle)) return false;
      const amt = r.amount == null ? null : Number(r.amount);
      if (min != null && !Number.isNaN(min) && (amt == null || amt < min - 0.005)) return false;
      if (max != null && !Number.isNaN(max) && (amt == null || amt > max + 0.005)) return false;
      return true;
    });
  }, [receipts, q, minAmt, maxAmt]);

  const total = filtered.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Receipts</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>The receipts vault is restricted to Paul and Jayda.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Receipt size={22} style={{ color: 'var(--hp-gold)' }} /> Receipts</h2>
          <p>{filtered.length} of {receipts.length} shown · {fmtMoney(total)} total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setDrawer({ receipt: null })}><Plus size={14} /> Add receipt</button>
        </div>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Search vendor / note</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
              <input className="input" style={{ ...inputStyle, paddingLeft: 30 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. fuel, BP, Bunnings…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>From date</label>
            <input type="date" className="input" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>To date</label>
            <input type="date" className="input" style={inputStyle} value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Min $</label>
            <input type="number" step="0.01" min="0" className="input" style={{ ...inputStyle, width: 100 }} value={minAmt} onChange={(e) => setMinAmt(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Max $</label>
            <input type="number" step="0.01" min="0" className="input" style={{ ...inputStyle, width: 100 }} value={maxAmt} onChange={(e) => setMaxAmt(e.target.value)} placeholder="0.00" />
          </div>
          {(from || to || q || minAmt || maxAmt) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(''); setTo(''); setQ(''); setMinAmt(''); setMaxAmt(''); }}>Clear</button>
          )}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading receipts…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>
              {receipts.length === 0 ? <>No receipts yet. Click <strong>Add receipt</strong> to scan your first one.</> : 'No receipts match your filters.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={th}>Date</th>
                    <th style={th}>Vendor</th>
                    <th style={th}>Note</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id}>
                      <td style={td}>{fmtDate(r.purchaseDate)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>
                        {r.vendor || '—'}
                        {r.contentType === 'application/pdf' && <FileText size={12} style={{ marginLeft: 6, color: 'var(--hp-dim)' }} title="PDF" />}
                        {r.ocrError ? <span title={`Auto-read failed: ${r.ocrError}`} style={{ color: 'var(--hp-amber, #f59e0b)' }}> ⚠</span> : ''}
                      </td>
                      <td style={{ ...td, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--hp-dim)' }} title={r.note}>{r.note || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.amount)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.fileViewUrl && <a href={r.fileViewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="View receipt"><ExternalLink size={13} /></a>}
                        {r.fileViewUrl && <button className="btn btn-ghost btn-sm" title="Email to accountant" onClick={() => setEmailFor(r)}><Mail size={13} /></button>}
                        {r.ocrError && r.fileViewUrl && <button className="btn btn-ghost btn-sm" title="Re-read receipt" onClick={() => reRead(r)}><RefreshCw size={13} /></button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setDrawer({ receipt: r })}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-red, #ef4444)' }} onClick={() => remove(r)}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <ReceiptDrawer
          receipt={drawer.receipt}
          currentUser={currentUser}
          addNotification={addNotification}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
      {emailFor && (
        <EmailModal
          receipt={emailFor}
          defaultEmail={accountantEmail}
          addNotification={addNotification}
          onClose={() => setEmailFor(null)}
          onSent={(remembered) => { if (remembered) setAccountantEmail(remembered); setEmailFor(null); }}
        />
      )}
    </>
  );
}
